import { useEffect, useState } from 'react';

import { LookupAutocompleteView } from '@/shared/ui/molecules/LookupAutocomplete';
import { useCompanySearchDropdown } from '../../../hooks/useCompanySearchDropdown';

export interface CompanyAutocompleteProps {
  /** ID da empresa selecionada; `null` quando nada. */
  readonly value: number | null;
  /** Rótulo da seleção corrente (exibição inicial em edição). */
  readonly selectedLabel?: string;
  /** Notifica o novo ID e rótulo (`null`/`''` ao limpar). */
  readonly onChange: (id: number | null, label: string) => void;
  readonly label: string;
  readonly placeholder: string;
  readonly hasError?: boolean;
}

/**
 * Autocomplete de empresa da prova (lookup `shared/companies`). Envolve o
 * componente customizado `LookupAutocompleteView` com a busca server-side de
 * empresas.
 *
 * @example
 * ```tsx
 * <CompanyAutocomplete
 *   value={form.companyId}
 *   selectedLabel={form.companyName ?? ''}
 *   onChange={(id, label) => onUpdate({ companyId: id, companyName: label })}
 *   label={t('form.fields.company')}
 *   placeholder={t('form.fields.companyPlaceholder')}
 * />
 * ```
 */
export function CompanyAutocomplete({
  value,
  selectedLabel: selectedLabelProp = '',
  onChange,
  label,
  placeholder,
  hasError,
}: CompanyAutocompleteProps) {
  const [search, setSearch] = useState('');
  const [inputText, setInputText] = useState(selectedLabelProp);
  const { options, isQuerySuccess } = useCompanySearchDropdown(search);

  // Sincroniza o rótulo exibido quando o pai o fornece após a montagem
  // (ex.: edição, cujo draft é populado depois que o autocomplete já montou).
  useEffect(() => {
    setInputText(selectedLabelProp);
  }, [selectedLabelProp]);

  return (
    <LookupAutocompleteView
      label={label}
      placeholder={placeholder}
      value={value !== null ? String(value) : ''}
      selectedLabel={inputText}
      options={options}
      isQuerySuccess={isQuerySuccess}
      search={search}
      hasError={hasError ?? false}
      onSearchChange={setSearch}
      onSelect={(id, itemLabel) => {
        onChange(id ? Number(id) : null, itemLabel);
        setInputText(itemLabel);
        setSearch('');
      }}
      onClear={() => {
        onChange(null, '');
        setInputText('');
        setSearch('');
      }}
    />
  );
}
